import type { PublicPracticeSong } from './types';

export const PUBLIC_PRACTICE_BOOTSTRAP_ID = 'public-practice-song-bootstrap';
export const PUBLIC_PRACTICE_BOOTSTRAP_SCHEMA_VERSION = 1;

const SONG_KEYS = [
  'song_key',
  'slug',
  'language',
  'title',
  'artist_name',
  'artist_slug',
  'artist_schema_type',
  'release_year',
  'genre',
  'difficulty',
  'vocal_range',
  'version_label',
  'composer',
  'seo_title',
  'meta_description',
  'hero_summary',
  'introduction',
  'bpm',
  'musical_key',
  'duration_sec',
  'practice_kind',
  'sections',
  'practice_steps',
  'limitations',
  'credits',
  'rights',
  'mr_path',
  'hub_path',
  'room_path',
  'audio_qa_at',
  'editorial_qa_at',
  'published_at',
] as const;

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function hasExactKeys(
  value: Record<string, unknown>,
  keys: readonly string[],
): boolean {
  const actual = Object.keys(value);
  return (
    actual.length === keys.length
    && actual.every((key) => keys.includes(key))
  );
}

function isNonEmptyString(value: unknown): value is string {
  return typeof value === 'string' && value.trim().length > 0;
}

function isFiniteNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

function isStringArray(value: unknown): value is string[] {
  return Array.isArray(value) && value.every(isNonEmptyString);
}

function isSection(value: unknown): boolean {
  if (
    !isObject(value)
    || !hasExactKeys(value, [
      'key',
      'label',
      'start_sec',
      'end_sec',
      'practice_goal',
    ])
  ) {
    return false;
  }
  return (
    isNonEmptyString(value.key)
    && isNonEmptyString(value.label)
    && isFiniteNumber(value.start_sec)
    && isFiniteNumber(value.end_sec)
    && value.start_sec >= 0
    && value.end_sec > value.start_sec
    && isNonEmptyString(value.practice_goal)
  );
}

function areSectionsWithinDuration(
  sections: unknown,
  durationSec: unknown,
): boolean {
  return (
    isFiniteNumber(durationSec)
    && durationSec > 0
    && Array.isArray(sections)
    && sections.every(isSection)
    && sections.every((section) => (
      isObject(section)
      && isFiniteNumber(section.end_sec)
      && section.end_sec <= durationSec
    ))
  );
}

function isCredits(value: unknown): boolean {
  return (
    isObject(value)
    && hasExactKeys(value, ['arranger', 'performers', 'master_owner'])
    && isNonEmptyString(value.arranger)
    && isStringArray(value.performers)
    && isNonEmptyString(value.master_owner)
  );
}

function isRight(value: unknown): boolean {
  return (
    isObject(value)
    && hasExactKeys(value, [
      'layer',
      'status',
      'basis',
      'source_url',
      'reviewed_at',
    ])
    && isNonEmptyString(value.layer)
    && isNonEmptyString(value.status)
    && isNonEmptyString(value.basis)
    && isNonEmptyString(value.source_url)
    && /^https?:\/\//.test(value.source_url)
    && isNonEmptyString(value.reviewed_at)
  );
}

export function parsePublicPracticeSongValue(
  value: unknown,
): PublicPracticeSong | null {
  if (!isObject(value) || !hasExactKeys(value, SONG_KEYS)) {
    return null;
  }

  const valid = (
    isNonEmptyString(value.song_key)
    && isNonEmptyString(value.slug)
    && value.language === 'ko'
    && isNonEmptyString(value.title)
    && isNonEmptyString(value.artist_name)
    && isNonEmptyString(value.artist_slug)
    && (
      value.artist_schema_type === 'Person'
      || value.artist_schema_type === 'MusicGroup'
    )
    && typeof value.release_year === 'number'
    && Number.isInteger(value.release_year)
    && value.release_year >= 1800
    && value.release_year <= 2100
    && isNonEmptyString(value.genre)
    && isNonEmptyString(value.difficulty)
    && ['beginner', 'intermediate', 'advanced'].includes(value.difficulty)
    && isNonEmptyString(value.vocal_range)
    && isNonEmptyString(value.version_label)
    && isNonEmptyString(value.composer)
    && isNonEmptyString(value.seo_title)
    && isNonEmptyString(value.meta_description)
    && isNonEmptyString(value.hero_summary)
    && isNonEmptyString(value.introduction)
    && isFiniteNumber(value.bpm)
    && value.bpm > 0
    && isNonEmptyString(value.musical_key)
    && isNonEmptyString(value.practice_kind)
    && areSectionsWithinDuration(value.sections, value.duration_sec)
    && isStringArray(value.practice_steps)
    && isStringArray(value.limitations)
    && isCredits(value.credits)
    && Array.isArray(value.rights)
    && value.rights.every(isRight)
    && isNonEmptyString(value.mr_path)
    && isNonEmptyString(value.hub_path)
    && isNonEmptyString(value.room_path)
    && isNonEmptyString(value.audio_qa_at)
    && isNonEmptyString(value.editorial_qa_at)
    && isNonEmptyString(value.published_at)
  );
  return valid ? value as unknown as PublicPracticeSong : null;
}

export function parsePublicPracticeBootstrap(route: {
  locale: string;
  slug: string;
  pathname: string;
}): PublicPracticeSong | null {
  const script = document.getElementById(PUBLIC_PRACTICE_BOOTSTRAP_ID);
  if (
    !(script instanceof HTMLScriptElement)
    || script.type !== 'application/json'
    || script.dataset.schemaVersion !== String(PUBLIC_PRACTICE_BOOTSTRAP_SCHEMA_VERSION)
  ) {
    return null;
  }

  try {
    const payload: unknown = JSON.parse(script.textContent ?? '');
    if (
      !isObject(payload)
      || !hasExactKeys(payload, ['schemaVersion', 'song'])
      || payload.schemaVersion !== PUBLIC_PRACTICE_BOOTSTRAP_SCHEMA_VERSION
    ) {
      return null;
    }
    const song = parsePublicPracticeSongValue(payload.song);
    if (
      song === null
      ||
      route.locale !== 'ko'
      || song.language !== route.locale
      || song.slug !== route.slug
      || song.hub_path !== route.pathname
      || song.hub_path !== `/ko/practice-songs/${song.slug}`
      || song.room_path !== `/ko/practice/${song.slug}`
      || song.mr_path !== `/api/public-practice-songs/${song.slug}/mr`
    ) {
      return null;
    }
    return song;
  } catch {
    return null;
  }
}
